/**
 * Epic Analytics Dashboard Component
 * 
 * Main dashboard component coordinating all epic analytics sub-components
 * with shared state management, error boundaries, and responsive layout.
 * 
 * Features:
 * - Project and time period filtering with URL synchronization
 * - Real-time data fetching with React Query caching
 * - Responsive layout (2/3 charts, 1/3 list on desktop; stacked on mobile)
 * - Comprehensive error handling with retry mechanisms
 * - Performance optimizations with memoization and lazy loading
 * - Accessibility compliance with ARIA labels and keyboard navigation
 * 
 * Requirements:
 * - 1.1: Main dashboard component coordinating all sub-components
 * - 5.5: Layout management for charts (2/3 width) and list (1/3 width)
 * - 6.2: Centralized state management and error handling
 * - 7.1: Data fetching coordination and loading states
 * - 7.4: Responsive design with vertical stacking below 768px
 * - 7.5: Accessibility features and keyboard navigation
 * 
 * @example
 * ```tsx
 * <EpicAnalyticsDashboard />
 * ```
 */

import React, { useState, useCallback, useMemo, useEffect, useRef, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { XProject } from '@jirax/common'
import { useApiQuery } from '../../../../utils/api'
import { useEpicAnalytics } from '../../../../utils/epicApi'
import { getEpicAnalysisUrl, getUserFriendlyErrorMessage } from '../../../../utils/epic-analytics-utils'
import { EpicAnalyticsFilters } from './EpicAnalyticsFilters'
import { EpicAnalyticsSummary } from './EpicAnalyticsSummary'
import { EpicPointsTimelineChart } from './EpicPointsTimelineChart'
import { EpicBubbleChart } from './EpicBubbleChart'
import { EpicAnalyticsListPanel } from './EpicAnalyticsListPanel'
import { EpicAnalyticsErrorBoundary } from './EpicAnalyticsErrorBoundary'
import EpicHeaderPills from '../../../Elements/EpicHeaderPills'

import { EpicAnalyticsDashboardProps, EpicTimelineDataPoint, EpicBubbleDataPoint, EpicAnalyticsData } from '../../../../types/epic-analytics-types'
import './EpicAnalyticsDashboard.css'

// Dashboard state interface for centralized state management
interface DashboardState {
  hoveredEpic: string | null
  lastSuccessfulData: EpicAnalyticsData | null
  retryCount: number
  isRetrying: boolean
}

/**
 * Epic Analytics Dashboard - Main coordinating component
 * Implements requirements 1.1, 5.5, 6.2, 7.1, 7.4
 * Memoized for performance optimization
 */
const EpicAnalyticsDashboard: React.FC<EpicAnalyticsDashboardProps> = memo(() => {
  const navigate = useNavigate()

  // Centralized dashboard state management (Requirement 6.2)
  const [dashboardState, setDashboardState] = useState<DashboardState>({
    hoveredEpic: null,
    lastSuccessfulData: null,
    retryCount: 0,
    isRetrying: false
  })

  // Refs for cleanup and performance
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)

  // Load projects for filter selection
  const {
    isLoading: projectsLoading,
    error: projectsError,
    data: projects = []
  } = useApiQuery<XProject[]>(['projects'])

  // State for filters using the same interface as Epic Analysis
  const [selectedProject, setSelectedProject] = useState<XProject | null>(null)
  const [dateRange, setDateRange] = useState({
    startDate: `${new Date().getFullYear()}-01-01`,
    endDate: `${new Date().getFullYear()}-12-31`
  })

  // Auto-select first project when projects load
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0])
    }
  }, [projects, selectedProject])

  // Convert date range to year/quarter for API compatibility
  const getYearFromDateRange = () => {
    const startDate = new Date(dateRange.startDate)
    return startDate.getFullYear()
  }

  const getQuarterFromDateRange = (): 'all' | 1 | 2 | 3 | 4 => {
    const startDate = new Date(dateRange.startDate)
    const endDate = new Date(dateRange.endDate)
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()

    // If spanning multiple years, consider it "all"
    if (startYear !== endYear) return 'all'

    const startMonth = startDate.getMonth() + 1 // 1-based
    const endMonth = endDate.getMonth() + 1

    // Check if it matches a quarter exactly
    if (startMonth === 1 && endMonth === 3) return 1
    if (startMonth === 4 && endMonth === 6) return 2
    if (startMonth === 7 && endMonth === 9) return 3
    if (startMonth === 10 && endMonth === 12) return 4

    // If it's the full year
    if (startMonth === 1 && endMonth === 12) return 'all'

    // Default to all if it doesn't match a standard quarter
    return 'all'
  }

  const filtersComplete = selectedProject !== null
  const filtersValid = filtersComplete

  // Fetch analytics data when filters are complete and valid (Requirement 7.1)
  const shouldFetchAnalytics = filtersComplete && filtersValid && !!selectedProject && !dashboardState.isRetrying

  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
    isFetching: analyticsFetching
  } = useEpicAnalytics({
    projectKey: selectedProject?.key || '',
    year: getYearFromDateRange(),
    quarter: getQuarterFromDateRange(),
    enabled: shouldFetchAnalytics
  })

  // Track if we're loading new data due to filter changes
  const isLoadingNewData = analyticsFetching && !analyticsData

  // Debug logging
  console.log('Dashboard state:', {
    filtersComplete,
    filtersValid,
    shouldFetchAnalytics,
    selectedProject,
    dateRange,
    projects: projects?.length || 0
  })

  // Update last successful data when new data arrives
  useEffect(() => {
    if (analyticsData && !analyticsError) {
      setDashboardState(prev => ({
        ...prev,
        lastSuccessfulData: analyticsData,
        retryCount: 0,
        isRetrying: false
      }))
    }
  }, [analyticsData, analyticsError])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [])

  // Memoized loading state (Requirement 7.1)
  const isLoading = useMemo(() => {
    return projectsLoading || analyticsLoading || dashboardState.isRetrying
  }, [projectsLoading, analyticsLoading, dashboardState.isRetrying])

  // Determine if filters should be disabled (during data fetching)
  const filtersDisabled = useMemo(() => {
    return projectsLoading || analyticsFetching || dashboardState.isRetrying
  }, [projectsLoading, analyticsFetching, dashboardState.isRetrying])

  // Memoized error state with user-friendly messages (Requirement 6.2)
  const error = useMemo(() => {
    const rawError = projectsError || analyticsError
    if (!rawError) return null

    return {
      ...rawError,
      userMessage: getUserFriendlyErrorMessage(rawError),
      canRetry: !projectsError && dashboardState.retryCount < 3
    }
  }, [projectsError, analyticsError, dashboardState.retryCount])

  // Data to display - use current data or fallback to last successful data
  const displayData = useMemo(() => {
    if (analyticsData && !analyticsError) {
      return analyticsData
    }

    // Show last successful data during retries or temporary errors
    if (dashboardState.lastSuccessfulData && (dashboardState.isRetrying || analyticsFetching)) {
      return dashboardState.lastSuccessfulData
    }

    return null
  }, [analyticsData, analyticsError, dashboardState.lastSuccessfulData, dashboardState.isRetrying, analyticsFetching])

  // Handle navigation to unanalyzed epics
  const handleUnanalyzedClick = useCallback(() => {
    if (!selectedProject) return

    const url = getEpicAnalysisUrl({
      projectKey: selectedProject.key,
      year: getYearFromDateRange(),
      quarter: getQuarterFromDateRange()
    })

    window.open(url, '_blank', 'noopener,noreferrer')
  }, [selectedProject, dateRange])

  // Enhanced retry mechanism with exponential backoff (Requirement 6.2)
  const handleRetry = useCallback(async () => {
    if (dashboardState.isRetrying || dashboardState.retryCount >= 3) return

    setDashboardState(prev => ({
      ...prev,
      isRetrying: true,
      retryCount: prev.retryCount + 1
    }))

    // Exponential backoff: 1s, 2s, 4s
    const delay = Math.pow(2, dashboardState.retryCount) * 1000

    retryTimeoutRef.current = setTimeout(async () => {
      if (!mountedRef.current) return

      try {
        await refetchAnalytics()
      } catch (error) {
        console.error('Retry failed:', error)
      } finally {
        if (mountedRef.current) {
          setDashboardState(prev => ({
            ...prev,
            isRetrying: false
          }))
        }
      }
    }, delay)
  }, [dashboardState.isRetrying, dashboardState.retryCount, refetchAnalytics])

  // Handle epic click from list panel
  const handleEpicClick = useCallback((epicKey: string) => {
    // Epic navigation is handled by the list panel component
    console.log('Epic clicked:', epicKey)
  }, [])

  // Handle chart hover interactions with centralized state
  const handleTimelineHover = useCallback((epic: EpicTimelineDataPoint) => {
    setDashboardState(prev => ({
      ...prev,
      hoveredEpic: epic.epicKey
    }))
  }, [])

  const handleBubbleHover = useCallback((epic: EpicBubbleDataPoint) => {
    setDashboardState(prev => ({
      ...prev,
      hoveredEpic: epic.epicKey
    }))
  }, [])

  // Clear hover state
  const clearHover = useCallback(() => {
    setDashboardState(prev => ({
      ...prev,
      hoveredEpic: null
    }))
  }, [])

  // Keyboard navigation support (Requirement 7.5)
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Handle escape key to clear hover states
    if (event.key === 'Escape') {
      clearHover()
    }

    // Handle tab navigation for better accessibility
    if (event.key === 'Tab') {
      // Let default tab behavior work, but ensure focus is visible
      const focusedElement = document.activeElement
      if (focusedElement) {
        focusedElement.classList.add('focus-visible')
      }
    }
  }, [clearHover])

  // Enhanced error state with retry logic (Requirement 6.2)
  if (error && !isLoading && !displayData) {
    return (
      <EpicAnalyticsErrorBoundary>
        <div className="epic-analytics-dashboard">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Failed to Load Analytics Data
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error.userMessage}</p>
                    {dashboardState.retryCount > 0 && (
                      <p className="mt-1 text-xs">
                        Retry attempt {dashboardState.retryCount} of 3
                      </p>
                    )}
                  </div>
                  <div className="mt-4 flex space-x-3">
                    {error.canRetry && (
                      <button
                        onClick={handleRetry}
                        disabled={dashboardState.isRetrying}
                        className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {dashboardState.isRetrying ? 'Retrying...' : 'Try Again'}
                      </button>
                    )}
                    <button
                      onClick={() => window.location.reload()}
                      className="bg-white px-3 py-2 rounded-md text-sm font-medium text-red-800 border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      Reload Page
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </EpicAnalyticsErrorBoundary>
    )
  }

  return (
    <EpicAnalyticsErrorBoundary>
      <div
        className="epic-analytics-dashboard"
        role="main"
        aria-label="Epic Analytics Dashboard"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">


          {/* Page Header with improved responsive typography */}
          <header className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                  Epic Analytics Dashboard
                </h1>
                <p className="text-sm sm:text-base text-gray-600 max-w-3xl">
                  Analyze epic completion trends, story points delivery, and quality metrics over time.
                </p>
              </div>
              <div className="flex-shrink-0">
                <EpicHeaderPills />
              </div>
            </div>
          </header>

          {/* Filters Section with accessibility */}
          <section
            aria-labelledby="epic-analytics-filters-section"
          >
            <EpicAnalyticsFilters
              selectedProject={selectedProject}
              dateRange={dateRange}
              projects={projects}
              onProjectChange={setSelectedProject}
              onDateRangeChange={setDateRange}
              loading={projectsLoading}
              disabled={filtersDisabled}
            />
          </section>

          {/* Loading indicator for filter changes */}
          {analyticsFetching && analyticsData && (
            <div
              className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3"
              role="status"
              aria-live="polite"
              aria-label="Loading new data"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div
                    className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"
                    aria-hidden="true"
                  ></div>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Loading updated analytics data...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area with responsive design */}
          {filtersComplete && filtersValid ? (
            <main aria-label="Analytics Data">
              {/* Summary Metrics with Error Boundary */}
              <section
                className="mb-6"
                aria-label="Summary Metrics"
              >
                <EpicAnalyticsErrorBoundary>
                  <EpicAnalyticsSummary
                    totalEpics={displayData?.summary.totalEpics || 0}
                    totalPoints={displayData?.summary.totalPoints || 0}
                    totalBugs={displayData?.summary.totalBugs || 0}
                    unanalyzedEpics={displayData?.summary.unanalyzedEpics || 0}
                    onUnanalyzedClick={handleUnanalyzedClick}
                    loading={isLoading && !displayData}
                  />
                </EpicAnalyticsErrorBoundary>
              </section>

              {/* Charts and List Layout - Requirement 5.5: 2/3 charts, 1/3 list */}
              {/* Requirement 7.4: Responsive design with vertical stacking below 768px */}
              <div className="flex flex-col md:flex-row gap-4 sm:gap-6 lg:gap-8">
                {/* Charts Section - 2/3 width on desktop, full width on mobile/tablet */}
                <section
                  className="md:w-2/3 space-y-4 sm:space-y-6 lg:space-y-8"
                  aria-label="Analytics Charts"
                >
                  {/* Points Timeline Chart with Error Boundary */}
                  <EpicAnalyticsErrorBoundary>
                    <div
                      onMouseLeave={clearHover}
                      onFocus={() => { }} // Ensure focusable for keyboard users
                      tabIndex={0}
                      role="region"
                      aria-label="Points Timeline Chart"
                    >
                      <EpicPointsTimelineChart
                        data={displayData?.timelineData || []}
                        loading={isLoading && !displayData}
                        onDataPointHover={handleTimelineHover}
                      />
                    </div>
                  </EpicAnalyticsErrorBoundary>

                  {/* Epic Bubble Chart with Error Boundary */}
                  <EpicAnalyticsErrorBoundary>
                    <div
                      onMouseLeave={clearHover}
                      onFocus={() => { }} // Ensure focusable for keyboard users
                      tabIndex={0}
                      role="region"
                      aria-label="Epic Bubble Chart"
                    >
                      <EpicBubbleChart
                        data={displayData?.bubbleData || []}
                        loading={isLoading && !displayData}
                        onBubbleHover={handleBubbleHover}
                      />
                    </div>
                  </EpicAnalyticsErrorBoundary>
                </section>

                {/* Epic List Panel - 1/3 width on desktop, full width on mobile/tablet */}
                {/* Height matches the combined height of both charts plus gap (h-64 + gap + h-64) */}
                <aside
                  className="md:w-1/3 flex-shrink-0 order-first md:order-last md:h-[48rem]"
                  aria-label="Epic List"
                >
                  <EpicAnalyticsErrorBoundary>
                    <EpicAnalyticsListPanel
                      epics={displayData?.epicsList || []}
                      loading={isLoading && !displayData}
                      onEpicClick={handleEpicClick}
                    />
                  </EpicAnalyticsErrorBoundary>
                </aside>
              </div>

              {/* Stale Data Warning with accessibility */}
              {displayData && dashboardState.lastSuccessfulData && analyticsFetching && (
                <div
                  className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4"
                  role="status"
                  aria-live="polite"
                  aria-label="Data refresh notification"
                >
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-amber-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">
                        Showing cached data while refreshing. New data will appear when available.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </main>
          ) : (
            /* Empty State - Show when filters are not complete */
            <main
              className="text-center py-8 sm:py-12"
              role="main"
              aria-label="Empty state"
            >
              <div className="max-w-md mx-auto px-4">
                <svg
                  className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <h2 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">
                  Select Project to View Analytics
                </h2>
                <p className="text-sm sm:text-base text-gray-500 mb-4">
                  Choose a project and time period to start exploring epic analytics data.
                </p>
                {projects.length === 0 && !projectsLoading && (
                  <div
                    className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3"
                    role="alert"
                    aria-live="polite"
                  >
                    No projects available. Please ensure you have access to at least one project.
                  </div>
                )}
              </div>
            </main>
          )}

          {/* Loading Overlay with accessibility */}
          {isLoading && filtersComplete && (
            <div
              className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50"
              role="dialog"
              aria-modal="true"
              aria-label="Loading analytics data"
            >
              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-lg max-w-sm mx-4">
                <div className="flex items-center space-x-3">
                  <div
                    className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-500"
                    aria-hidden="true"
                  ></div>
                  <span className="text-sm sm:text-base text-gray-700">
                    Loading analytics data...
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Skip to main content link for screen readers */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-md z-50"
          >
            Skip to main content
          </a>
        </div>
      </div>
    </EpicAnalyticsErrorBoundary>
  )
})

EpicAnalyticsDashboard.displayName = 'EpicAnalyticsDashboard'

export { EpicAnalyticsDashboard }
export default EpicAnalyticsDashboard